import { ConversationMessageType } from "@/enum/conversation.enum";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import type { ImportedConversation } from "@signalist/shared-types";

/** The viewer: the importer of a row whose importer name the API could not give. */
export interface ImportedThreadOwner {
	id: number;
	firstName?: string | null;
	lastName?: string | null;
}

/**
 * One imported message (PREC-4305) as a chat-log conversation: a plain message in the direction
 * it was written, marked `importedAt`, with nothing a synced row carries that would make the
 * chat act on it — no sentiment, no step, no campaign, no schedule.
 *
 * Its id is negated: imported rows come from their own table, so their ids overlap the synced
 * thread's, and the thread keys and anchors on `id`.
 *
 * An imported LinkedIn message its importer sent names the importer as its user (it was read from
 * their own account) — on a shared lead that can be a colleague (Andreas, 2026-10-10). A sent mail
 * names its real sender from the envelope. `importedByName` and `importedPrivate` let the bubble
 * say who imported it and who can see it.
 */
const importerOf = (row: ImportedConversation, viewer: ImportedThreadOwner) =>
	row.importedBy ??
	(row.userId === viewer.id
		? { id: viewer.id, firstName: viewer.firstName ?? null, lastName: viewer.lastName ?? null }
		: null);

const nameOf = (importer: { firstName: string | null; lastName: string | null } | null) => {
	const name = [importer?.firstName, importer?.lastName].filter(Boolean).join(" ").trim();

	return name || null;
};

export const importedToThreadConversation = (
	row: ImportedConversation,
	viewer: ImportedThreadOwner
): Conversation => {
	const importer = importerOf(row, viewer);

	return {
		id: -row.id,
		threadId: "",
		type: row.type,
		emailId: row.emailId,
		seenAt: null,
		sentAt: row.sentAt,
		openedAt: null,
		message: row.message ?? "",
		createdAt: row.importedAt,
		updatedAt: row.importedAt,
		user:
			row.type === "message_sent" && importer
				? {
						id: importer.id,
						firstName: importer.firstName ?? "",
						lastName: importer.lastName ?? "",
					}
				: null,
		userId: row.userId,
		stepId: null,
		receiverEmail: row.receiverEmail,
		senderEmail: row.senderEmail,
		senderLinkedinUrl: row.senderLinkedinUrl,
		receiverLinkedinUrl: row.receiverLinkedinUrl,
		campaignId: null,
		scheduled: false,
		scheduledFor: null,
		messageType: ConversationMessageType.MESSAGE,
		sentiment: null,
		listPersonId: null,
		listId: null,
		prospectId: null,
		isOtherAccount: false,
		senderName: row.senderName,
		ccReceiverEmail: null,
		subject: row.subject,
		importedAt: row.importedAt,
		importedByName: row.importedBy ? nameOf(row.importedBy) : null,
		importedPrivate: row.isPrivate,
	} as unknown as Conversation;
};

/**
 * The lead's thread with the viewer's imported earlier conversation woven in by when each
 * message was written. Without imported messages the thread is returned as it came, so nothing
 * downstream sees a new array.
 */
export const mergeImportedIntoThread = (
	conversations: Conversation[] | undefined,
	imported: ImportedConversation[],
	viewer: ImportedThreadOwner
): Conversation[] | undefined => {
	if (imported.length === 0) {
		return conversations;
	}

	return [
		...(conversations ?? []),
		...imported.map((row) => importedToThreadConversation(row, viewer)),
	]
		.map((conversation, index) => ({ conversation, index }))
		.sort(
			(a, b) =>
				new Date(a.conversation.sentAt).getTime() - new Date(b.conversation.sentAt).getTime() ||
				a.index - b.index
		)
		.map(({ conversation }) => conversation);
};
